import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, Check } from 'lucide-react';
import { forgotPassword, ApiException } from '../api/client';
import { Karte } from '../components/leitstand';

/**
 * Passwort vergessen — laeuft vor der Anmeldung, darum die mittige Karte
 * im Leitstand-Stil ohne Rahmen und ohne Navigation.
 *
 * Die Rueckmeldung bleibt bewusst unbestimmt ("falls ein Konto existiert"):
 * Sonst verriete die Seite, welche Adressen im System stehen.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.data.code === 'RATE_LIMITED') {
          setError('Zu viele Anfragen. Bitte in ein paar Minuten noch einmal versuchen.');
        } else {
          setError(
            err.data.error ||
              'Die E-Mail lässt sich gerade nicht senden. Bitte später noch einmal versuchen.',
          );
        }
      } else {
        setError('Keine Verbindung zum Server. Bitte noch einmal versuchen.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
          wash<b className="font-semibold text-akzent">·</b>ops
        </p>

        <Karte titel="Passwort vergessen">
          {sent ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-flaeche-2 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-lauf" />
              </div>
              <p className="text-tinte font-medium mb-2">E-Mail gesendet</p>
              <p className="text-tinte-2 text-sm mb-6">
                Falls ein Konto mit dieser E-Mail-Adresse existiert, haben Sie eine E-Mail mit einem
                Link zum Zurücksetzen Ihres Passworts erhalten.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-tinte-2 transition-colors hover:text-tinte"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Anmeldung
              </Link>
            </div>
          ) : (
            <>
              <p className="text-tinte-2 text-sm mb-6 text-center">
                Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Link zum Zurücksetzen Ihres
                Passworts.
              </p>

              {error && (
                <div className="mb-4 rounded-md border border-stoer bg-flaeche-2 p-3 text-center text-stoer">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-tinte-2 mb-1">
                    E-Mail
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tinte-3" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 rounded-md border border-linie-2 bg-flaeche-2 text-tinte focus:border-akzent focus:outline-none"
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full rounded-md border border-akzent bg-akzent px-4 py-2 font-medium text-akzent-satz transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wird gesendet...
                    </span>
                  ) : (
                    'Link senden'
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-tinte-3 transition-colors hover:text-tinte-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zurück zur Anmeldung
                </Link>
              </div>
            </>
          )}
        </Karte>
      </div>
    </div>
  );
}
